import React, { useEffect, useState } from 'react';
import { Heart, AlertTriangle, CheckCircle2, ArrowLeft } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useLang } from '../context/LanguageContext';
import EmailMatchWarning from '../components/EmailMatchWarning';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUGGESTED = [10, 25, 50, 100];

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30;

const ShowNameField = ({ t, showName, setShowName, displayName, setDisplayName }) => (
  <div className="mt-2">
    <label className="flex items-center gap-3 cursor-pointer select-none">
      <input
        type="checkbox"
        data-testid="donate-show-name-checkbox"
        checked={showName}
        onChange={(e) => setShowName(e.target.checked)}
        className="w-4 h-4 accent-[#f5b400]"
      />
      <span className="font-body text-sm text-white/80">{t('donate.show_name')}</span>
    </label>
    {showName && (
      <input
        type="text"
        data-testid="donate-display-name-input"
        value={displayName}
        maxLength={40}
        onChange={(e) => setDisplayName(e.target.value)}
        placeholder={t('donate.display_name_placeholder')}
        className="mt-3 w-full bg-black/50 border border-white/15 focus:border-[#f5b400] outline-none rounded-md px-3 py-2 text-white text-sm placeholder:text-white/30 transition-colors"
      />
    )}
  </div>
);

const DonatePage = () => {
  const { toast } = useToast();
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const [amount, setAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');
  const [showName, setShowName] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Handle return from Stripe
  useEffect(() => {
    const success = params.get('success');
    const canceled = params.get('canceled');
    const sessionId = params.get('session_id');
    if (canceled) {
      toast({ title: t('donate.canceled_title'), description: t('donate.canceled_body') });
      setParams({}, { replace: true });
      return;
    }
    if (success && sessionId) {
      let attempts = 0;
      const poll = async () => {
        try {
          const { data } = await axios.get(`${API}/checkout/status/${encodeURIComponent(sessionId)}`);
          if (data.payment_status === 'paid' || data.status === 'expired') {
            setResult(data);
            setParams({}, { replace: true });
            return;
          }
          if (attempts++ < MAX_POLLS) setTimeout(poll, POLL_INTERVAL_MS);
          else {
            setResult({ ...data });
            setParams({}, { replace: true });
          }
        } catch (e) {
          setResult({ error: e.response?.data?.detail || e.message || t('donate.toast_verify_failed') });
          setParams({}, { replace: true });
        }
      };
      setResult({ payment_status: 'processing', status: 'open' });
      poll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finalAmount = customAmount
    ? Math.max(1, parseFloat(customAmount) || 0)
    : amount;

  const donate = async () => {
    if (!finalAmount || finalAmount < 1) {
      toast({ title: t('donate.toast_pick_title'), description: t('donate.toast_pick_body'), variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/checkout/donation`, {
        origin_url: window.location.origin,
        amount_eur: finalAmount,
        message: message || undefined,
        show_name: showName,
        display_name: showName ? displayName.trim() || undefined : undefined,
      });
      window.location.href = data.url;
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || t('donate.toast_failed_body');
      toast({ title: t('donate.toast_failed_title'), description: msg, variant: 'destructive' });
      setLoading(false);
    }
  };

  return (
    <div className="pt-28 pb-20 bg-[#0a0608] min-h-screen">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#f5b400] font-nav text-xs hover:text-white transition-colors"
        >
          <ArrowLeft size={14} /> {t('donate.back')}
        </Link>

        <div className="mt-6 text-center fade-up">
          <p className="font-nav text-[#f5b400] text-xs tracking-widest">{t('donate.eyebrow')}</p>
          <h1 className="font-heading section-heading-white mt-3">{t('donate.title_top')}</h1>
          <h1 className="font-heading section-heading-gold">{t('donate.title_bottom')}</h1>
          <p className="mt-4 max-w-xl mx-auto text-white/75 font-body text-sm">
            {t('donate.intro')}
          </p>
        </div>

        <div className="mt-10 rounded-2xl border border-[#f5b400]/30 bg-gradient-to-br from-black/70 via-[#1a1410]/60 to-black/70 p-7 md:p-10">
          <EmailMatchWarning />
          <h3 className="font-heading text-white text-xl text-center">{t('donate.choose_amount')}</h3>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {SUGGESTED.map((a) => {
              const selected = !customAmount && a === amount;
              return (
                <button
                  key={a}
                  onClick={() => {
                    setAmount(a);
                    setCustomAmount('');
                  }}
                  className={`py-5 rounded-xl border font-heading text-xl transition-all ${
                    selected
                      ? 'border-[#f5b400] bg-[#f5b400]/15 text-[#f5b400] shadow-[0_0_20px_-8px_rgba(245,180,0,0.6)]'
                      : 'border-white/15 bg-black/40 text-white hover:border-[#f5b400]/60'
                  }`}
                >
                  €{a}
                </button>
              );
            })}
          </div>

          <div className="mt-5">
            <label className="font-nav text-[10px] tracking-widest text-white/55">
              {t('donate.or_custom')}
            </label>
            <div className="mt-1 flex items-center gap-2 bg-black/50 border border-white/15 focus-within:border-[#f5b400] rounded-md px-3 py-2 transition-colors">
              <span className="font-heading text-white/80">€</span>
              <input
                type="number"
                min={1}
                step={1}
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                placeholder="50"
                className="flex-1 bg-transparent outline-none text-white text-sm placeholder:text-white/30"
              />
            </div>
          </div>

          <div className="mt-5">
            <label className="font-nav text-[10px] tracking-widest text-white/55">
              {t('donate.message_optional')}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 200))}
              rows={3}
              placeholder={t('donate.message_placeholder')}
              className="mt-1 w-full bg-black/50 border border-white/15 focus:border-[#f5b400] outline-none rounded-md px-3 py-2 text-white text-sm placeholder:text-white/30 transition-colors resize-none"
            />
            <p className="text-right text-white/40 text-[10px] font-body mt-1">
              {message.length}/200
            </p>
          </div>

          <ShowNameField t={t} showName={showName} setShowName={setShowName} displayName={displayName} setDisplayName={setDisplayName} />

          <button
            data-testid="donate-submit-button"
            onClick={donate}
            disabled={loading || !finalAmount || finalAmount < 1}
            className="mt-6 w-full bg-[#f5b400] hover:bg-[#ffc630] disabled:opacity-60 text-black font-nav text-xs py-4 rounded-md inline-flex items-center justify-center gap-2 transition-colors"
          >
            <Heart size={16} fill="currentColor" />
            {loading ? t('donate.button_redirecting') : `${t('donate.button_donate')} €${finalAmount.toFixed(2)}`}
          </button>

          <p className="mt-4 text-center text-white/45 text-[10px] font-body">
            {t('donate.secure_note')}
          </p>
        </div>

        <div className="mt-6 text-center">
          <Link
            to="/supporters"
            data-testid="donate-supporters-wall-link"
            className="inline-flex items-center gap-2 font-nav text-xs tracking-widest text-[#f5b400] hover:text-white transition-colors"
          >
            <Heart size={14} /> {t('donate.see_wall')}
          </Link>
        </div>

        {result && (
          <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 fade-up">
            <div className="relative w-full max-w-md rounded-2xl border border-[#f5b400]/40 bg-gradient-to-br from-[#1a1410] via-black to-black p-7 text-center">
              {result.error ? (
                <>
                  <AlertTriangle size={48} className="text-red-400 mx-auto" />
                  <h3 className="font-heading text-white text-2xl mt-4">{t('donate.result.could_not_verify')}</h3>
                  <p className="font-body text-white/75 text-sm mt-3">{result.error}</p>
                </>
              ) : result.payment_status === 'paid' ? (
                <>
                  <CheckCircle2 size={56} className="text-[#f5b400] mx-auto" />
                  <h3 className="font-heading text-white text-2xl mt-4">{t('donate.result.thank_you')}</h3>
                  <p className="font-body text-white/75 text-sm mt-3">
                    {t('donate.result.thank_you_body')}
                  </p>
                </>
              ) : result.status === 'expired' ? (
                <>
                  <AlertTriangle size={48} className="text-red-400 mx-auto" />
                  <h3 className="font-heading text-white text-2xl mt-4">{t('donate.result.expired')}</h3>
                  <p className="font-body text-white/75 text-sm mt-3">
                    {t('donate.result.expired_body')}
                  </p>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 mx-auto border-2 border-[#f5b400] border-t-transparent rounded-full animate-spin" />
                  <h3 className="font-heading text-white text-2xl mt-4">{t('donate.result.processing')}</h3>
                  <p className="font-body text-white/75 text-sm mt-3">
                    {t('donate.result.processing_body')}
                  </p>
                </>
              )}
              <button
                onClick={() => setResult(null)}
                className="mt-6 bg-[#f5b400] hover:bg-[#ffc630] text-black font-nav text-xs px-6 py-3 rounded-md transition-colors"
              >
                {t('donate.result.close')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DonatePage;
